import { useState } from "react";
import FloatingPanelService from "../FloatingPanel";
import { capitalize } from "../../../utils/FormatUtils";

type Item = {
    title: string;
    component?: () => JSX.Element;
    hash?: string;
    hasItems?: boolean;
    items?: Item[];
}

const flattenItems = (items: Item[]): { item: Item; index: number; displayTitle: string; level: number }[] => {
    let result: { item: Item; index: number; displayTitle: string; level: number }[] = [];
    let index = 0;

    const recurse = (items: Item[], prefix = "", level = 0) => {
        for (let item of items) {
            if (item.hasItems && item.items) {
                result.push({ item, index: -1, displayTitle: item.title, level });
                recurse(item.items, prefix + "-", level + 1);
            } else {
                result.push({ item, index: index++, displayTitle: prefix + item.title, level });
            }
        }
    };

    recurse(items);
    return result;
};


const VerticalMenu = ({ items, title }: { items: Item[], title: string }) => {
    const flatItems = flattenItems(items);
    const [index, setIndex] = useState(() => {
        const hashNoHash = window.location.hash.substring(1);
        return Math.max(0, flatItems.findIndex(x => x.item.hash === hashNoHash && x.index !== -1));
    });
    return (
        <div className="d-flex">
            <nav className="nav-sidebar" style={{ width: '100%', minWidth: 280, maxWidth: 280 }}>
                <ul className="list-unstyled nav flex-column" role="tablist">
                    <li className="menu-title-content">
                        <h2 className="menu-title text-start">{title.split(' ').map(x => capitalize(x)).join(' ')}</h2>
                    </li>
                    <hr className="menu-separator" />
                    {flatItems.map(({ item, index: flatIndex, displayTitle, level }, i) => {
                        const titleSimple = item.title.replaceAll(" ", "").replaceAll('(', "").replaceAll(")", "");

                        if (item.hasItems) {
                            return (
                                <li
                                    className={`nav-item${level > 0 ? " ms-3" : ""}`}
                                    role="presentation"
                                    key={titleSimple}
                                >
                                    <button className={"btn btn-link fw-bold text-start"} style={{ cursor: 'default' }}>
                                        {item.title}
                                    </button>
                                </li>
                            );
                        }

                        return (
                            <li
                                className={`nav-item${level > 0 ? " ms-3" : ""}`}
                                role="presentation"
                                key={titleSimple}
                            >
                                <button
                                    type="button"
                                    className={
                                        "btn btn-link nav-link text-start" +
                                        (flatIndex === index ? " active" : "")
                                    }
                                    id={"tab-" + titleSimple}
                                    data-bs-toggle="tab"
                                    data-bs-target={"#" + titleSimple + "-tab-pane"}
                                    onClick={() => {
                                        window.location.hash = item.hash ?? "";
                                        FloatingPanelService.hidePanel();
                                        setIndex(flatIndex);
                                    }}
                                    role="tab"
                                    aria-controls={titleSimple + "-tab-pane"}
                                >
                                    {item.title}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </nav>

            <div className="tab-content p-3 w-100 mw-65p mw-65w">
                {flatItems
                    .filter(x => x.index !== -1)
                    .map(({ item, index: flatIndex }) => {
                        const titleSimple = item.title.replaceAll(" ", "");
                        const Component = item.component!;
                        return (
                            <div
                                key={titleSimple}
                                className={"tab-pane fade" + (flatIndex === index ? " show active" : "")}
                                id={titleSimple + "-tab-pane"}
                                role="tabpanel"
                                aria-labelledby={"tab-" + titleSimple}
                                tabIndex={flatIndex}
                            >
                                <Component />
                            </div>
                        );
                    })}
            </div>
        </div >
    );
};

export default VerticalMenu;